import { useEffect, useState } from "react";
import {
  DollarSign, TrendingUp, Users, Briefcase, Target,
  Save, X, Plus, Edit2, Trash2, Percent, ChevronDown, ChevronUp,
  Settings as SettingsIcon, Eye, Receipt, Zap,
} from "lucide-react";
import {
  useFinanceStore,
  type ResourceCost,
  type ConnectCost,
  type ProjectTypeOverride,
  type ProjectBreakdown,
} from "../store/useFinanceStore";
import LoaderSpinner from "../components/ui/LoaderSpinner";
import toast from "react-hot-toast";

const ACCOUNT_AVATAR: Record<string, string> = {
  "Irfan Baig": "bg-orange-100 text-orange-700",
  Nadeem: "bg-sky-100 text-sky-700",
  Farah: "bg-violet-100 text-violet-700",
  Rizwan: "bg-rose-100 text-rose-700",
};

const PROJECT_TYPES = [
  "Website",
  "Graphic Design",
  "Video Animation",
  "Marketing",
  "Trial-based",
];

const fmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${(n / 1_000).toFixed(1)}k`
  : `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const FinancePage = () => {
  const {
    accounts, totals, isFetching,
    fetchSummary, fetchSettings, updateAccountFinance,
  } = useFinanceStore();

  const [editingAcc, setEditingAcc] = useState<string | null>(null);
  const [feeDraft, setFeeDraft] = useState("");
  const [commDraft, setCommDraft] = useState("");
  const [expandedAcc, setExpandedAcc] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [costsModal, setCostsModal] = useState<{ project: ProjectBreakdown } | null>(null);
  const [summaryModal, setSummaryModal] = useState<{ projectId: string; name: string } | null>(null);

  useEffect(() => {
    fetchSummary();
    fetchSettings();
  }, []);

  if (isFetching && !accounts.length) {
    return <div className="py-16 flex justify-center"><LoaderSpinner size={48} /></div>;
  }

  const brand = "bg-[linear-gradient(90deg,#03488A_0%,#00AAF1_100%)]";
  const totalRoi = totals && totals.connectsUsed ? totals.netProfit / totals.connectsUsed : 0;

  return (
    <div className="min-h-screen pt-20 bg-[#f8f9ff]">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8 space-y-8">

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
              Performance & Finance
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">
              Per-account profitability, deductions, true cost, and net profit.
            </p>
          </div>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 ${
              showSettings
                ? "bg-[#03488A] text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            <SettingsIcon size={15} />
            Financial Settings
          </button>
        </div>

        {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiCard label="Revenue" value={fmt(totals?.revenue || 0)} icon={DollarSign} accent="from-emerald-500 to-green-600" />
          <KpiCard label="Net Profit" value={fmt(totals?.netProfit || 0)} icon={TrendingUp} accent="from-violet-500 to-purple-600" />
          <KpiCard label="Connects" value={String(totals?.connectsUsed || 0)} icon={Users} accent="from-sky-500 to-blue-600" />
          <KpiCard label="Jobs Won" value={String(totals?.jobsWon || 0)} icon={Briefcase} accent="from-amber-500 to-orange-600" />
          <KpiCard label="ROI / Connect" value={fmt(totalRoi)} icon={Target} accent="from-rose-500 to-pink-600" />
        </div>

        <div className="space-y-4">
          <h2 className="text-xl font-extrabold text-slate-800">Account Profitability</h2>

          {accounts.map((acc) => {
            const isEditing = editingAcc === acc.accountId;
            const isExpanded = expandedAcc === acc.accountId;
            const profitColor = acc.netProfit >= 0 ? "text-emerald-600" : "text-red-600";

            return (
              <div key={acc.accountId} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">

                <div className="p-5 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full grid place-items-center text-base font-bold ${ACCOUNT_AVATAR[acc.accountName] || "bg-slate-200 text-slate-700"}`}>
                      {acc.accountName.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{acc.accountName}</h3>
                      <p className="text-xs text-slate-500">
                        {acc.totalBids} bids • {acc.jobsWon} won ({acc.winRate}%) • {acc.usedConnects}/{acc.totalConnects} connects
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 flex-wrap">
                    <Stat label="Revenue" value={fmt(acc.revenue)} />
                    <Stat label="Deductions" value={fmt(acc.totalDeductions)} sub />
                    <Stat label="Total Cost" value={fmt(acc.totalCost)} sub />
                    <Stat label="Net Profit" value={fmt(acc.netProfit)} valueClassName={profitColor} />
                    <Stat label="ROI/Connect" value={fmt(acc.roi)} />

                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-slate-500">Fee</span>
                          <input type="number" className="w-14 px-1.5 py-1 border border-slate-300 rounded text-xs" value={feeDraft} onChange={e => setFeeDraft(e.target.value)} />
                          <Percent size={11} className="text-slate-400" />
                        </div>
                        <div className="flex items-center gap-1 text-xs ml-2">
                          <span className="text-slate-500">Comm</span>
                          <input type="number" className="w-14 px-1.5 py-1 border border-slate-300 rounded text-xs" value={commDraft} onChange={e => setCommDraft(e.target.value)} />
                          <Percent size={11} className="text-slate-400" />
                        </div>
                        <button
                          onClick={async () => {
                            await updateAccountFinance(acc.accountId, Number(feeDraft) || 0, Number(commDraft) || 0);
                            setEditingAcc(null);
                          }}
                          className={`${brand} text-white p-1.5 rounded-md`}
                        ><Save size={13} /></button>
                        <button onClick={() => setEditingAcc(null)} className="p-1.5 rounded-md border border-slate-200 text-slate-500"><X size={13} /></button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingAcc(acc.accountId);
                          setFeeDraft(String(acc.taxPercent));
                          setCommDraft(String(acc.commissionPercent));
                        }}
                        className="text-xs text-slate-500 hover:text-[#03488A] flex items-center gap-1"
                        title="Account-level fee & commission overrides"
                      >
                        <Edit2 size={11} /> Fee {acc.taxPercent}% · Comm {acc.commissionPercent}%
                      </button>
                    )}

                    <button
                      onClick={() => setExpandedAcc(isExpanded ? null : acc.accountId)}
                      className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                    >
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50/50 p-5">
                    {acc.projects.length === 0 ? (
                      <p className="text-xs text-slate-500 italic">No projects linked to this account yet.</p>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                <th className="py-2">Project</th>
                                <th className="py-2">Type</th>
                                <th className="py-2 text-right">Price</th>
                                <th className="py-2 text-right">Upwork Fee</th>
                                <th className="py-2 text-right">Commission</th>
                                <th className="py-2 text-right">Retained</th>
                                <th className="py-2 text-right">Labor</th>
                                <th className="py-2 text-right">Connect</th>
                                <th className="py-2 text-right">Net Profit</th>
                                <th className="py-2 text-center">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {acc.projects.map((p) => (
                                <tr key={p._id}>
                                  <td className="py-2 font-semibold text-slate-700">{p.name}</td>
                                  <td className="py-2 text-slate-500">{p.projectType}</td>
                                  <td className="py-2 text-right">{fmt(p.price)}</td>
                                  <td className="py-2 text-right text-rose-500">−{fmt(p.upworkFeeAmount)}</td>
                                  <td className="py-2 text-right text-rose-500">−{fmt(p.commissionAmount)}</td>
                                  <td className="py-2 text-right font-semibold text-slate-700">{fmt(p.retainedRevenue)}</td>
                                  <td className="py-2 text-right text-rose-500">−{fmt(p.laborCost)}</td>
                                  <td className="py-2 text-right text-rose-500">−{fmt(p.connectCostAmount)}</td>
                                  <td className={`py-2 text-right font-bold ${p.netProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(p.netProfit)}</td>
                                  <td className="py-2 text-center">
                                    <div className="flex justify-center gap-1">
                                      <button
                                        onClick={() => setSummaryModal({ projectId: p._id, name: p.name })}
                                        className="p-1 rounded border border-slate-200 hover:bg-white"
                                        title="View financial summary"
                                      >
                                        <Eye size={12} />
                                      </button>
                                      <button
                                        onClick={() => setCostsModal({ project: p })}
                                        className="p-1 rounded border border-slate-200 hover:bg-white"
                                        title="Edit costs"
                                      >
                                        <Edit2 size={12} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="mt-4 pt-3 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <FooterStat label="Total Revenue" value={fmt(acc.revenue)} />
                          <FooterStat label="Total Deductions" value={fmt(acc.totalDeductions)} />
                          <FooterStat label="Total Cost" value={fmt(acc.totalCost)} />
                          <FooterStat label="Avg Profit / Project" value={fmt(acc.avgProfitPerProject)} highlight />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {costsModal && (
        <ResourceCostsModal
          project={costsModal.project}
          onClose={() => setCostsModal(null)}
        />
      )}
      {summaryModal && (
        <FinancialSummaryModal
          projectId={summaryModal.projectId}
          projectName={summaryModal.name}
          onClose={() => setSummaryModal(null)}
        />
      )}
    </div>
  );
};

export default FinancePage;

const KpiCard = ({ label, value, icon: Icon, accent }: any) => (
  <div className="bg-white rounded-2xl border border-slate-200/60 p-4 shadow-sm">
    <div className={`p-2 rounded-lg bg-gradient-to-br ${accent} w-fit shadow-sm mb-3`}>
      <Icon size={16} className="text-white" />
    </div>
    <p className="text-2xl font-bold text-slate-900">{value}</p>
    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mt-1">{label}</p>
  </div>
);

const Stat = ({ label, value, sub, valueClassName = "" }: any) => (
  <div className="text-right">
    <p className={`text-sm font-bold ${sub ? "text-slate-500" : "text-slate-900"} ${valueClassName}`}>{value}</p>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
  </div>
);

const FooterStat = ({ label, value, highlight = false }: any) => (
  <div className={`p-3 rounded-xl ${highlight ? "bg-gradient-to-r from-[#03488A] to-[#00AAF1] text-white" : "bg-white border border-slate-200"}`}>
    <p className={`text-[10px] font-bold uppercase tracking-wider ${highlight ? "text-white/80" : "text-slate-500"}`}>{label}</p>
    <p className={`text-base font-extrabold ${highlight ? "text-white" : "text-slate-900"} mt-0.5`}>{value}</p>
  </div>
);

const NumField = ({ label, value, onChange, prefix, suffix, step }: any) => (
  <div>
    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</label>
    <div className="flex items-center gap-1 mt-1 px-2 py-1.5 border border-slate-200 rounded-lg bg-white">
      {prefix && <span className="text-xs text-slate-400">{prefix}</span>}
      <input
        type="number"
        step={step || "0.01"}
        value={value}
        onChange={e => onChange(Number(e.target.value) || 0)}
        className="w-full text-sm font-semibold text-slate-900 outline-none"
      />
      {suffix && <span className="text-xs text-slate-400">{suffix}</span>}
    </div>
  </div>
);

const SettingsPanel = ({ onClose }: { onClose: () => void }) => {
  const { settings, updateSettings, isSaving } = useFinanceStore();
  const [draft, setDraft] = useState({
    globalUpworkFee: settings?.globalUpworkFee ?? 10,
    globalCommission: settings?.globalCommission ?? 10,
    defaultCostPerConnect: settings?.defaultCostPerConnect ?? 0.165,
    defaultAvgConnectsPerWin: settings?.defaultAvgConnectsPerWin ?? 0,
    projectTypeOverrides: settings?.projectTypeOverrides ?? [],
  });

  useEffect(() => {
    if (settings) {
      setDraft({
        globalUpworkFee: settings.globalUpworkFee,
        globalCommission: settings.globalCommission,
        defaultCostPerConnect: settings.defaultCostPerConnect,
        defaultAvgConnectsPerWin: settings.defaultAvgConnectsPerWin,
        projectTypeOverrides: settings.projectTypeOverrides || [],
      });
    }
  }, [settings]);

  const addOverride = () => {
    const usedTypes = draft.projectTypeOverrides.map(o => o.projectType);
    const next = PROJECT_TYPES.find(t => !usedTypes.includes(t));
    if (!next) return toast.error("All project types already configured");
    setDraft({
      ...draft,
      projectTypeOverrides: [
        ...draft.projectTypeOverrides,
        { projectType: next, upworkFee: null, commission: null, defaultResources: [] },
      ],
    });
  };

  const updateOverride = (i: number, patch: Partial<ProjectTypeOverride>) =>
    setDraft({
      ...draft,
      projectTypeOverrides: draft.projectTypeOverrides.map((o, idx) =>
        idx === i ? { ...o, ...patch } : o
      ),
    });

  const removeOverride = (i: number) =>
    setDraft({
      ...draft,
      projectTypeOverrides: draft.projectTypeOverrides.filter((_, idx) => idx !== i),
    });

  const save = () => updateSettings(draft);

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-extrabold text-slate-900">Financial Configuration</h2>
          <p className="text-xs text-slate-500">Global defaults apply to all projects unless overridden per type or per project.</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500"><X size={16} /></button>
      </div>

      <div>
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Global Defaults</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NumField
            label="Upwork Fee" suffix="%"
            value={draft.globalUpworkFee}
            onChange={(v: number) => setDraft({ ...draft, globalUpworkFee: v })}
          />
          <NumField
            label="Account Commission" suffix="%"
            value={draft.globalCommission}
            onChange={(v: number) => setDraft({ ...draft, globalCommission: v })}
          />
          <NumField
            label="Cost per Connect" prefix="$"
            value={draft.defaultCostPerConnect}
            onChange={(v: number) => setDraft({ ...draft, defaultCostPerConnect: v })}
            step="0.001"
          />
          <NumField
            label="Avg Connects / Win"
            value={draft.defaultAvgConnectsPerWin}
            onChange={(v: number) => setDraft({ ...draft, defaultAvgConnectsPerWin: v })}
            step="1"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Project-Type Overrides</h3>
          <button onClick={addOverride} className="text-xs font-semibold text-[#03488A] hover:underline flex items-center gap-1">
            <Plus size={12} /> Add Override
          </button>
        </div>

        {draft.projectTypeOverrides.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No overrides — all project types use global defaults.</p>
        ) : (
          <div className="space-y-2">
            {draft.projectTypeOverrides.map((o, i) => (
              <div key={i} className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50/50">
                <select
                  className="px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white font-semibold"
                  value={o.projectType}
                  onChange={e => updateOverride(i, { projectType: e.target.value })}
                >
                  {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-slate-500">Fee</span>
                  <input
                    type="number"
                    placeholder="—"
                    className="w-16 px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                    value={o.upworkFee ?? ""}
                    onChange={e => updateOverride(i, { upworkFee: e.target.value === "" ? null : Number(e.target.value) })}
                  />
                  <Percent size={10} className="text-slate-400" />
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-slate-500">Comm</span>
                  <input
                    type="number"
                    placeholder="—"
                    className="w-16 px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                    value={o.commission ?? ""}
                    onChange={e => updateOverride(i, { commission: e.target.value === "" ? null : Number(e.target.value) })}
                  />
                  <Percent size={10} className="text-slate-400" />
                </div>
                <div className="flex-1" />
                <button onClick={() => removeOverride(i)} className="p-1.5 rounded-md border border-slate-200 text-red-500 hover:bg-red-50"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-2 italic">Empty fields fall back to global defaults.</p>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
        <button
          onClick={save}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#03488A] to-[#00AAF1] text-white hover:opacity-95 disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

const ResourceCostsModal = ({
  project, onClose,
}: {
  project: ProjectBreakdown;
  onClose: () => void;
}) => {
  const { updateProjectCosts, settings } = useFinanceStore();
  const [costs, setCosts] = useState<ResourceCost[]>(project.resourceCosts || []);
  const [connectCost, setConnectCost] = useState<ConnectCost>({
    avgConnectsPerWin: project.connectCost?.avgConnectsPerWin || 0,
    costPerConnect:
      project.connectCost?.costPerConnect || settings?.defaultCostPerConnect || 0.165,
  });
  const [projectPrice, setProjectPrice] = useState<string>(
    project.projectPrice != null ? String(project.projectPrice) : ""
  );
  const [saving, setSaving] = useState(false);

  const addRow = () =>
    setCosts([...costs, { label: "", type: "hourly", amount: 0, hours: 0 }]);
  const updateRow = (i: number, patch: Partial<ResourceCost>) =>
    setCosts(costs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const removeRow = (i: number) => setCosts(costs.filter((_, idx) => idx !== i));

  const priceForCalc =
    projectPrice !== ""
      ? Number(projectPrice)
      : (project.escrowAmount || 0) + (project.releasedPayment || 0);

  const laborCost = costs.reduce((sum, rc) => {
    if (rc.type === "fixed") return sum + (rc.amount || 0);
    if (rc.type === "hourly") return sum + (rc.amount || 0) * (rc.hours || 0);
    if (rc.type === "percentage") return sum + (priceForCalc * (rc.amount || 0)) / 100;
    return sum;
  }, 0);

  const connectCostTotal = (connectCost.avgConnectsPerWin || 0) * (connectCost.costPerConnect || 0);
  const totalCost = laborCost + connectCostTotal;

  const save = async () => {
    if (costs.some(c => !c.label.trim())) {
      return toast.error("Each resource needs a label");
    }
    setSaving(true);
    try {
      await updateProjectCosts(project._id, {
        resourceCosts: costs,
        connectCost,
        projectPrice: projectPrice === "" ? null : Number(projectPrice),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Cost Configuration</p>
            <h2 className="text-lg font-bold text-slate-900">{project.name}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto p-6 space-y-6">

          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Project Price</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1 px-3 py-2 border border-slate-200 rounded-lg bg-white w-44">
                <span className="text-sm text-slate-400">$</span>
                <input
                  type="number"
                  placeholder="Auto"
                  value={projectPrice}
                  onChange={e => setProjectPrice(e.target.value)}
                  className="w-full text-sm font-semibold text-slate-900 outline-none"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Empty = use Escrow + Released ({fmt((project.escrowAmount || 0) + (project.releasedPayment || 0))})
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Receipt size={12} /> Resource Costs (Labor)
              </h3>
              <p className="text-[11px] text-slate-500">Labor: <span className="font-bold text-slate-700">{fmt(laborCost)}</span></p>
            </div>

            {costs.length === 0 && (
              <p className="text-center text-xs text-slate-400 italic py-4">No resources yet. Add one below.</p>
            )}

            {costs.length > 0 && (
              <div className="flex items-center gap-2 px-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                <span className="flex-1">Resource</span>
                <span className="w-24">Type</span>
                <span className="w-20 text-right">Rate</span>
                <span className="w-16 text-right">Hours</span>
                <span className="w-20 text-right">Cost</span>
                <span className="w-7"></span>
              </div>
            )}

            <div className="space-y-2">
              {costs.map((c, i) => {
                const rowCost =
                  c.type === "fixed" ? (c.amount || 0)
                  : c.type === "hourly" ? (c.amount || 0) * (c.hours || 0)
                  : (priceForCalc * (c.amount || 0)) / 100;

                return (
                  <div key={i} className="flex items-center gap-2 p-2.5 rounded-xl border border-slate-200 bg-slate-50/50">
                    <input
                      placeholder="e.g. Designer"
                      className="flex-1 px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white"
                      value={c.label}
                      onChange={e => updateRow(i, { label: e.target.value })}
                    />
                    <select
                      className="w-24 px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white"
                      value={c.type}
                      onChange={e => updateRow(i, { type: e.target.value as any })}
                    >
                      <option value="hourly">Hourly</option>
                      <option value="fixed">Fixed $</option>
                      <option value="percentage">% Revenue</option>
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      placeholder={c.type === "percentage" ? "%" : "$"}
                      className="w-20 px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white text-right"
                      value={c.amount}
                      onChange={e => updateRow(i, { amount: Number(e.target.value) || 0 })}
                    />
                    <input
                      type="number"
                      step="0.5"
                      placeholder="hrs"
                      disabled={c.type !== "hourly"}
                      className="w-16 px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white text-right disabled:bg-slate-100 disabled:text-slate-400"
                      value={c.hours || 0}
                      onChange={e => updateRow(i, { hours: Number(e.target.value) || 0 })}
                    />
                    <span className="w-20 text-right text-xs font-bold text-slate-700">{fmt(rowCost)}</span>
                    <button onClick={() => removeRow(i)} className="p-1 rounded-md border border-slate-200 text-red-500 hover:bg-red-50"><Trash2 size={12} /></button>
                  </div>
                );
              })}
            </div>

            <button onClick={addRow} className="mt-2 w-full py-2 border-2 border-dashed border-slate-300 rounded-xl text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-[#03488A] flex items-center justify-center gap-1">
              <Plus size={14} /> Add Resource
            </button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Zap size={12} /> Connect Cost (Acquisition)
              </h3>
              <p className="text-[11px] text-slate-500">Connect Cost: <span className="font-bold text-slate-700">{fmt(connectCostTotal)}</span></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumField
                label="Avg Connects per Win"
                value={connectCost.avgConnectsPerWin}
                onChange={(v: number) => setConnectCost({ ...connectCost, avgConnectsPerWin: v })}
                step="1"
              />
              <NumField
                label="Cost per Connect" prefix="$"
                value={connectCost.costPerConnect}
                onChange={(v: number) => setConnectCost({ ...connectCost, costPerConnect: v })}
                step="0.001"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#03488A] to-[#00AAF1] rounded-xl p-4 text-white">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold opacity-90">Labor Cost</span>
              <span className="font-bold">{fmt(laborCost)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="font-semibold opacity-90">Connect Cost</span>
              <span className="font-bold">{fmt(connectCostTotal)}</span>
            </div>
            <div className="border-t border-white/30 mt-2 pt-2 flex items-center justify-between">
              <span className="text-base font-bold">Total Project Cost</span>
              <span className="text-xl font-extrabold">{fmt(totalCost)}</span>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#03488A] to-[#00AAF1] text-white hover:opacity-95 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Costs"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FinancialSummaryModal = ({
  projectId, projectName, onClose,
}: {
  projectId: string;
  projectName: string;
  onClose: () => void;
}) => {
  const { fetchProjectSummary } = useFinanceStore();
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    fetchProjectSummary(projectId).then(setData);
  }, [projectId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Financial Summary</p>
            <h2 className="text-lg font-bold text-slate-900">{projectName}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500"><X size={18} /></button>
        </div>

        {!data ? (
          <div className="p-12 flex justify-center"><LoaderSpinner size={36} /></div>
        ) : (
          <div className="p-6 space-y-4">

            <div className="space-y-1.5">
              <SummaryRow label="Project Price" value={fmt(data.price)} bold />
              <SummaryRow label={`Upwork Fee (${data.upworkFeePercent}%)`} value={`−${fmt(data.upworkFeeAmount)}`} negative />
              <SummaryRow label={`Commission (${data.commissionPercent}%)`} value={`−${fmt(data.commissionAmount)}`} negative />
              <div className="border-t border-slate-200 pt-1.5">
                <SummaryRow label="Retained Revenue" value={fmt(data.retainedRevenue)} bold />
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 space-y-1.5">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Operational Costs</p>
              <SummaryRow label="Labor Cost" value={fmt(data.laborCost)} negative />
              <SummaryRow label="Connect Cost" value={fmt(data.connectCost)} negative />
              <div className="border-t border-slate-200 pt-1.5">
                <SummaryRow label="Total Cost" value={fmt(data.totalCost)} bold />
              </div>
            </div>

            <div className={`rounded-xl p-5 text-white ${data.netProfit >= 0 ? "bg-gradient-to-r from-emerald-500 to-green-600" : "bg-gradient-to-r from-red-500 to-rose-600"}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold opacity-90">Net Profit</span>
                <span className="text-2xl font-extrabold">{fmt(data.netProfit)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value, bold, negative }: any) => (
  <div className="flex items-center justify-between text-sm">
    <span className={bold ? "font-bold text-slate-900" : "text-slate-600"}>{label}</span>
    <span className={`${bold ? "font-extrabold text-slate-900" : "font-semibold"} ${negative ? "text-rose-500" : ""}`}>{value}</span>
  </div>
);
